import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { getLocation, newRef, SERVICE } from "@/lib/util";
import { BackBar, Field } from "@/components/common";
import PaymentPicker from "@/components/PaymentPicker";
import PhotoPicker from "@/components/PhotoPicker";
import CarPlateInput from "@/components/CarPlateInput";
import { tr as t } from "@/lib/i18n";

const GOODS = ["أثاث منزل", "أجهزة كهربائية", "عفش متنوع", "مواد بناء خفيفة", "بضائع محل", "أخرى"];

export async function uploadPhotos(tripId, files, stage, loc) {
  if (!files.length) return;
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  fd.append("stage", stage);
  fd.append("loc", JSON.stringify(loc));
  await api.post(`/trips/${tripId}/photos`, fd);
}

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
        className={`h-11 px-4 rounded-full border-2 font-bold ${value === o ? "border-blue-700 bg-blue-700 text-white" : "border-slate-200 bg-white text-slate-700"}`}>{t(o)}</button>
    ))}
  </div>
);

function TypeChooser({ types, setType }) {
  return (
    <div>
      <BackBar title={t("نقلة جديدة")} />
      <div className="p-4 space-y-4">
        <p className="text-xl font-bold text-center my-4">{t("ما نوع النقلة؟")}</p>
        {types.map((x) => (
          <button key={x} data-testid={`choose-type-${x}`} onClick={() => setType(x)}
            className="card w-full p-6 flex items-center gap-5 border-2 border-blue-100 active:scale-[0.98]">
            <span className="w-20 h-20 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center"><Truck className="w-12 h-12" /></span>
            <span className="text-start text-2xl font-black font-head">{t(SERVICE[x] || x)}</span>
          </button>
        ))}
        {!types.length && <p className="text-center text-red-600">{t("لم يتم تحديد نوع عملك. تواصل مع الإدارة.")}</p>}
      </div>
    </div>
  );
}

export default function NewTrip() {
  const { user } = useAuth();
  const nav = useNavigate();
  const types = user.driver?.work_types || [];
  const [type, setType] = useState(types.length === 1 ? types[0] : null);
  const ref = useMemo(newRef, []);
  const [f, setF] = useState({ requester_phone: "", requester_name: "", car_plate: "", car_type: "", goods_type: "", load_desc: "",
    pickup: "", dropoff: "", price: "", payment_status: "unpaid", paid_amount: "", payment_method: "cash" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(t("جاري تحديد الموقع..."));
    const loc = await getLocation();
    try {
      setBusy(t("جاري حفظ النقلة..."));
      const { data } = await api.post("/trips", { ...f, service_type: type, client_ref: ref, loc });
      if (photos.length) {
        setBusy(t("جاري رفع الصور..."));
        try { await uploadPhotos(data.id, photos, "before", loc); } catch (e2) { toast.error(t("تم حفظ النقلة لكن تعذر رفع الصور: ") + errMsg(e2)); }
      }
      toast.success(t("تم حفظ النقلة {n}", { n: data.number }));
      nav(`/driver/trips/${data.id}`, { replace: true });
    } catch (e2) {
      toast.error(errMsg(e2));
      setBusy("");
    }
  };

  if (!type) return <TypeChooser types={types} setType={setType} />;

  return (
    <div>
      <BackBar title={`${t("نقلة جديدة")} — ${t(SERVICE[type] || type)}`} />
      <form onSubmit={submit} className="p-4 space-y-5" data-testid="new-trip-form">
        {!user.driver?.vehicle && <div className="card p-3 bg-amber-50 border-amber-200 text-amber-900">{t("لا توجد شاحنة مرتبطة بحسابك. ستُحفظ النقلة بدون رقم شاحنة.")}</div>}
        <div className="card p-4 space-y-4">
          <Field label={t("رقم هاتف طالب النقل")} required>
            <input data-testid="trip-requester-phone" className="field ltr text-left" inputMode="tel" value={f.requester_phone} onChange={set("requester_phone")} required />
          </Field>
          <Field label={t("اسم طالب النقل (اختياري)")}>
            <input data-testid="trip-requester-name" className="field" value={f.requester_name} onChange={set("requester_name")} />
          </Field>
        </div>
        <div className="card p-4 space-y-4">
          <Field label={t("نوع الحمولة")}><Chips testid="goods-type" options={GOODS} value={f.goods_type} onChange={(v) => setF({ ...f, goods_type: v })} /></Field>
          <Field label={t("وصف الحمولة")}>
            <textarea data-testid="trip-load-desc" className="field" value={f.load_desc} onChange={set("load_desc")} placeholder={t("مثال: غرفة نوم + ثلاجة")} />
          </Field>
          <Field label={t("لوحة المركبة المنقولة (اختياري)")}>
            <CarPlateInput optional testid="trip-car-plate" value={f.car_plate} onChange={(v) => setF({ ...f, car_plate: v })} />
          </Field>
        </div>
        <div className="card p-4 space-y-4">
          <Field label={t("موقع التحميل")} required>
            <input data-testid="trip-pickup" className="field" value={f.pickup} onChange={set("pickup")} placeholder={t("مثال: الشويخ الصناعية")} required />
          </Field>
          <Field label={t("موقع التسليم")} required>
            <input data-testid="trip-dropoff" className="field" value={f.dropoff} onChange={set("dropoff")} placeholder={t("مثال: الجهراء")} required />
          </Field>
        </div>
        <div className="card p-4">
          <Field label={t("صور الحمولة قبل التحميل")}><PhotoPicker files={photos} onChange={setPhotos} /></Field>
        </div>
        <div className="card p-4 space-y-4">
          <Field label={t("سعر النقلة (دينار كويتي)")} required>
            <input data-testid="trip-price" className="field ltr text-left text-2xl font-black" inputMode="decimal" placeholder="0.000" value={f.price} onChange={set("price")} required />
          </Field>
          <PaymentPicker value={f} onChange={(v) => setF({ ...f, ...v })} />
        </div>
        <button data-testid="trip-submit-button" disabled={!!busy} className="btn btn-lg btn-blue w-full sticky bottom-3">
          {busy ? <><Loader2 className="w-6 h-6 animate-spin" />{busy}</> : <><CheckCircle2 className="w-7 h-7" />{t("حفظ النقلة")}</>}
        </button>
      </form>
    </div>
  );
}
